<template>
  <v-container pa-0>
    <v-row>
      <div class="page__title">
        <h1>Usługi</h1>

        <div>
          Poniżej znajdziesz listę usług oferowanych przez naszych Partnerów. Aby skorzystać ze zniżek musisz posiadać
          Ustecką Kartę Mieszkańca.
        </div>
      </div>
    </v-row>

    <list-wrapper :total="count" :per-page="12">
      <v-row>
        <v-col cols="12">
          <div slot="filters" class="filters">
            <v-expansion-panels flat>
              <v-expansion-panel>
                <v-expansion-panel-header v-slot="{ open }">
                  <v-row no-gutters>
                    <v-col cols="1">
                      <h4 class="my-2">
                        Filtry
                      </h4>
                    </v-col>

                    <v-col cols="11" class="text--secondary">
                      <div v-if="!open" style="width: 100%">
                        <v-chip v-if="dateRangeStart" outlined>
                          <strong> Od:</strong>&nbsp;{{ dateRangeStart }}
                        </v-chip>

                        <v-chip v-if="dateRangeEnd" outlined> <strong> Do:</strong>&nbsp; {{ dateRangeEnd }} </v-chip>

                        <v-chip v-if="categories.length" outlined>
                          <strong> Kategorie:</strong>&nbsp;
                          {{ categoryNameList.join(', ') }}
                        </v-chip>

                        <v-chip v-if="selectedType" outlined>
                          <strong> Rodzaj:</strong>&nbsp;{{ selectedTypeName }}
                        </v-chip>

                        <v-chip cols="1" outlined>
                          {{ !intendedForUser ? 'Wszystkie usługi' : 'Usługi przeznaczone dla mnie' }}
                        </v-chip>
                      </div>
                    </v-col>
                  </v-row>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                  <v-row>
                    <v-col cols="12" lg="6" xl="3">
                      <v-select
                        v-model="categories"
                        dense
                        multiple
                        outlined
                        clearable
                        item-text="name"
                        item-value="id"
                        label="Kategorie"
                        :items="categoryList"
                        @input="setCategories"
                      >
                        <template v-slot:selection="{ attrs, item, parent, selected }">
                          <v-chip v-if="item === Object(item)" v-bind="attrs" :input-value="selected.id" label small>
                            <span class="pr-2">
                              {{ item.name }}
                            </span>

                            <v-icon small @click="parent.selectItem(item)">
                              close
                            </v-icon>
                          </v-chip>
                        </template>
                      </v-select>
                    </v-col>

                    <v-col cols="12" lg="6" xl="3">
                      <v-select
                        v-model="selectedType"
                        dense
                        outlined
                        clearable
                        hide-selected
                        label="Rodzaj usługi"
                        :items="benefitTypes"
                        @input="setSelectedType"
                      />
                    </v-col>

                    <v-col cols="12" lg="6" xl="3">
                      <v-menu
                        v-model="dateRangeStartMenu"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                        :close-on-content-click="false"
                        :nudge-right="40"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="dateRangeStart"
                            clearable
                            label="Od"
                            dense
                            outlined
                            prepend-inner-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            @input="setDateRangeStart"
                          />
                        </template>

                        <v-date-picker v-model="dateRangeStart" :max="dateRangeEnd" @input="setDateRangeStart" />
                      </v-menu>
                    </v-col>

                    <v-col cols="12" lg="6" xl="3">
                      <v-menu
                        v-model="dateRangeEndMenu"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                        :close-on-content-click="false"
                        :nudge-right="40"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="dateRangeEnd"
                            clearable
                            label="Do"
                            dense
                            outlined
                            prepend-inner-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            @input="setDateRangeEnd"
                          />
                        </template>

                        <v-date-picker v-model="dateRangeEnd" :min="dateRangeStart" @input="setDateRangeEnd" />
                      </v-menu>
                    </v-col>

                    <v-col cols="12" lg="6" xl="3">
                      <v-checkbox
                        v-model="intendedForUser"
                        label="Tylko usługi przeznaczone dla mnie"
                        @click="setIntendedForUser"
                      />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-col>
      </v-row>

      <v-row v-if="count">
        <v-col v-for="(event, index) in events" :key="index" cols="12" lg="6" xl="4">
          <event :event="event" />
        </v-col>
      </v-row>

      <v-row v-else>
        <v-col>
          <p>- Brak wyników -</p>
        </v-col>
      </v-row>
    </list-wrapper>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';
import { BenefitType, DisplayBenefitTypeTranslation } from '@witkac/kartamiejska-shared';
import { tryParseInt } from '@witkac/utils/tryParseInt';

import Event from '@/components/cards/Event';
import ListWrapper from '@/components/list/ListWrapper';
import { listMixin } from '@/mixins/listMixin';
import { benefitFilters } from '@/utils/benefit';

// @vuese
// Lista eventów
// @group views
export default {
  components: { Event, ListWrapper },
  mixins: [listMixin('benefit', 12)],
  data() {
    const categories =
      this.$store.state.category.list.filter(
        category =>
          this.$route.query &&
          this.$route.query.categories &&
          (this.$route.query.categories.includes(category.id) ||
            this.$route.query.categories.includes(category.id + ''))
      ) || [];

    const benefitTypes = [
      { text: DisplayBenefitTypeTranslation.AMOUNT_DISCOUNT, value: BenefitType.AMOUNT_DISCOUNT },
      { text: DisplayBenefitTypeTranslation.PERCENT_DISCOUNT, value: BenefitType.PERCENT_DISCOUNT },
      { text: DisplayBenefitTypeTranslation.FIXED_COST, value: BenefitType.FIXED_COST }
    ];

    return {
      intendedForUser: false,
      categories,
      benefitTypes,
      dateRangeStart: this.$route.query.dateRangeStart,
      dateRangeEnd: this.$route.query.dateRangeEnd,
      dateRangeStartMenu: false,
      dateRangeEndMenu: false,
      selectedType: +this.$route.query.benefitType || undefined
    };
  },
  computed: {
    ...mapState({
      events: state => state.benefit.list,
      count: state => state.benefit.count,
      categoryList: state => state.category.list
    }),
    categoryNameList() {
      return this.categories.map(
        selected => this.categoryList.find(category => category.id === selected || category.id === selected.id).name
      );
    },
    selectedTypeName() {
      return this.benefitTypes.find(type => type.value === this.selectedType).text;
    }
  },
  methods: {
    // @vuese
    // Ustawia filtr kategorii
    setCategories(categories) {
      this.$router.replace({ query: { ...this.$route.query, categories, page: 1 } });
    },
    // @vuese
    // Ustawia filtr rodzaju zniżki
    setSelectedType(benefitType) {
      this.$router.replace({ query: { ...this.$route.query, benefitType, page: 1 } });
    },
    // @vuese
    // Ustawia filtr daty `od`
    setDateRangeStart(dateRangeStart) {
      this.dateRangeStartMenu = false;
      this.$router.replace({ query: { ...this.$route.query, dateRangeStart, page: 1 } });
    },
    // @vuese
    // Ustawia filtr daty `do`
    setDateRangeEnd(dateRangeEnd) {
      this.dateRangeEndMenu = false;
      this.$router.replace({ query: { ...this.$route.query, dateRangeEnd, page: 1 } });
    },
    // @vuese
    // Ustawia filtr przeznaczonych usług
    setIntendedForUser() {
      const intendedForUser = this.intendedForUser ? 1 : 0;
      this.$router.replace({ query: { ...this.$route.query, intendedForUser, page: 1 } });
    },
    // @vuese
    // Aktualizuje listę
    updateList(route) {
      const filters = {};
      if (route.query.categories && route.query.categories.length) {
        filters.categories = route.query.categories.map(category => tryParseInt(category));
      }
      if (route.query.benefitType) {
        filters.benefitType = tryParseInt(route.query.benefitType);
      }
      if (tryParseInt(route.query.intendedForUser)) {
        filters.intendedForUser = true;
      }
      if (route.query.dateRangeStart) {
        filters.dateRangeStart = route.query.dateRangeStart;
      }
      if (route.query.dateRangeEnd) {
        filters.dateRangeEnd = route.query.dateRangeEnd;
      }
      return this.$store.dispatch(`benefit/list`, {
        ...benefitFilters(route, filters)
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.v-chip__content > strong {
  padding-right: 5px !important;
}

.filters .v-text-field__details {
  display: none;
}

.v-expansion-panel {
  border: solid 1px $color-border;
  border-radius: 0 !important;
}

.v-chip {
  margin-left: 12px;
  margin-bottom: 8px;
}

.page__title div {
  padding: 0 16px;
}
</style>
